import { PackageModulesFormController } from '../controller/PackageModulesFormController';
import { BaseWebComponentElement } from '../../../../framework/BaseWebComponentElement';
import { DeepPartial } from 'typeorm';
import { ModulesData, PackageData } from '../../../types/package-data';
import { Observable } from 'rxjs';
import { Feature } from '@nirvana/shared/constants';
import { PackageActionContext } from '../../types/PackageActionContext';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import _ from 'lodash';
import { PackageType } from '../../../packagesGrid/types';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { KitTypes } from '../../../downloadKitButtonSelector/constants';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';

export class PackageModulesFormWebComponent extends BaseWebComponentElement {
  public controller!: PackageModulesFormController;
  private availableFeatures: Feature[];
  private _showAllModules = false;
  private _useDefaultValues = true;
  private _selectedKitType: KitTypes | null = null;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: PackageModulesFormController,
    });
  }

  public set showAllModules(showAllModules: boolean) {
    this._showAllModules = showAllModules;
  }

  public get showAllModules(): boolean {
    return this._showAllModules;
  }

  public get useDefaultValues(): boolean {
    return this._useDefaultValues;
  }

  public set useDefaultValues(useDefaultValues: boolean) {
    this._useDefaultValues = useDefaultValues;
  }

  public get selectedKitType(): KitTypes | null {
    return this._selectedKitType;
  }

  public set selectedKitType(selectedKitType: KitTypes | null) {
    this._selectedKitType = selectedKitType;
  }

  public showAllModulesToggle(isVisible: boolean): void {
    toggleVisibility(this.controller.view.dom.$showAllModulesToggle, isVisible);
    if (!isVisible && this.controller.view.dom.$showAllModulesToggle.checked) {
      this.controller.view.dom.$showAllModulesToggle.setFormValue(false);
    }
  }

  public setAvailableFeatures(availableFeatures: Feature[]): void {
    this.availableFeatures = availableFeatures;
  }

  public getAvailableFeatures(): Feature[] {
    return this.availableFeatures;
  }

  public set disableRelayCheckboxModule(state: boolean) {
    this.controller.disableRelayCheckboxModule(state);
  }

  public set toggleOffRelayModule(state: boolean) {
    this.controller.toggleOffRelayModule(state);
  }

  public get value(): DeepPartial<PackageData> {
    const formValues: object = this.controller.view.formGroup.value;

    for (const moduleField of Object.values(this.controller.view.modulesFields)) {
      if (moduleField.$uiCheckbox.classList.contains('hidden')) {
        delete formValues['settings']['modules'][moduleField.key];
      }
    }

    if (this.controller.view.dom.$patchManagementServerCheckbox.classList.contains('hidden')) {
      delete formValues['settings']['modules']['patchManagementServer'];
    }

    if (this.controller.view.dom.$mailServersCheckbox.classList.contains('hidden')) {
      delete formValues['settings']['modules']['mailServers'];
    }

    return _.merge(formValues, { settings: { productType: this.operationMode } });
  }

  public checkIfAtLeastOnModuleIsChecked(): { valid: boolean } {
    return this.controller.view.checkIfAtLeastOnModuleIsChecked();
  }

  public get valid$(): Observable<boolean> {
    return this.controller.view.formGroup.valid$;
  }

  public get formContext(): PackageActionContext {
    const context = this.getAttribute('context');

    if (!PackageActionContext.isValid(context)) {
      throw new Error(`PackagePanel context not valid: ${context}`);
    }

    return context as PackageActionContext;
  }

  public get isRelayForFrat(): boolean {
    return this.controller.view.dom.$updateServerCheckbox.value;
  }

  public set formContext(context: PackageActionContext) {
    this.setAttribute('context', context);
  }

  public set packageData(packageData: ModulesData) {
    this.controller.setPackageData(packageData);
  }

  public set packageType(packageType: PackageType) {
    this.controller.setPackageType(packageType);
  }

  public get hasUnsavedChanges(): boolean {
    return this.controller.view.formGroup.hasUnsavedChanges;
  }

  public get operationMode(): MonthlyProductType {
    return this.controller.view.dom.$operationModeFormField.Input.value as number;
  }

  public get getDefaultOperationMode(): MonthlyProductType {
    return this.controller.getDefaultOperationMode();
  }

  public set displayOnlyModules(value: boolean) {
    if (value) {
      this.setAttribute('data-display-only-modules', '');
    } else {
      this.removeAttribute('data-display-only-modules');
    }
  }

  public get displayOnlyModules(): boolean {
    return this.hasAttribute('data-display-only-modules');
  }

  public setReadonlyModules(readonly: boolean, modules: Array<string>): void {
    for (const module of modules) {
      const moduleUiCheckbox = this.controller.view.formGroup.getFormChild(["settings", "modules", module]) as GzFormFieldWebComponent;
      if (moduleUiCheckbox) {
        moduleUiCheckbox.readonly = readonly;
      }
    }
  }

  public setValueModules(value: boolean, modules: Array<string>): void {
    for (const module of modules) {
      const moduleUiCheckbox = this.controller.view.formGroup.getFormChild(["settings", "modules", module]) as GzFormFieldWebComponent;
      if (moduleUiCheckbox) {
        moduleUiCheckbox.value = value;
      }
    }
  }
}
